import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

const API_BASE = '/api/v1/account'

async function apiFetch<T = unknown>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options?.headers },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText })) as { error?: string }
    throw new Error(body.error || 'Request failed')
  }
  return res.json() as Promise<T>
}

export interface BalanceResponse {
  balance: string // formatted e.g. "$5.00"
  balance_cents: number
}

export interface Transaction {
  id: string
  amount: string // formatted
  amount_cents: number
  type: string
  description: string
  order_id: string | null
  balance_after: string
  balance_after_cents: number
  created_at: string
}

export function useBalance() {
  return useQuery<BalanceResponse>({
    queryKey: ['balance'],
    queryFn: () => apiFetch('/balance'),
  })
}

export function useTransactions() {
  return useQuery<{ transactions: Transaction[] }>({
    queryKey: ['transactions'],
    queryFn: () => apiFetch('/transactions'),
  })
}

export function useTopup() {
  const qc = useQueryClient()
  return useMutation<{ checkout_url: string; session_id: string }, Error, number>({
    mutationFn: (amountCents: number) =>
      apiFetch('/topup', { method: 'POST', body: JSON.stringify({ amount_cents: amountCents }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['balance'] }),
  })
}
